'use client';

import React, { useMemo } from 'react';
import classNames from 'classnames';
import { z } from 'zod';
import type { Writable } from 'type-fest';
import { internalAnalytics } from '$util/analytics/analyticsService/client';
import { AnalyticsServiceFieldKey } from '$util/analytics/analyticsService/types';

import { HeadingLevels } from '$src-components/atoms/HeadingLevels';
import { Card } from '$src-components/atoms/Card';
import { ContentComponentSection } from '$src-components/atoms/ContentComponentSection';

import type { SchemaType } from '$util/schema';
import { makeContentComponentDefaultConfig } from '$util/makeContentComponentDefaultConfig';
import type { ContentComponent } from '$cms/ContentComponent';
import { CmsLinkSchema, CmsRichTextSchema, CmsShortTextSchema } from '$cms/types';
import { showLiveChat } from '$components/LiveChat';
import { Button, DesignType } from '$src-components/atoms/Button';
import { Col, Row } from '$src-components/atoms/Grid';
import type { ColProps } from '$src-components/atoms/Grid/types';
import { Img } from '$src-components/atoms/Img';

import { renderRichTextContent } from '@/cms/richTextContentHelpers';
import styles from './index.module.scss';

const sendInternalAnalytic = (fieldKey: AnalyticsServiceFieldKey) =>
  internalAnalytics.sendCalloutCardContactUsEvents(fieldKey);

export type CalloutCardProps = Omit<ColProps, 'children'>;

const defaultConfiguration = makeContentComponentDefaultConfig({
  backgroundColor: undefined,
  fontColor: undefined,
  trackAsContactCard: undefined,
});

export const CalloutCardSchema = {
  translatedEntities: z.object({
    heading: z.union([CmsRichTextSchema, CmsShortTextSchema]),
    body: CmsShortTextSchema.optional(),
    ctaLink: CmsLinkSchema.optional(),
    ctaIcon: CmsShortTextSchema.optional(),
    firstLink: CmsLinkSchema.optional(),
    firstLinkIcon: CmsShortTextSchema.optional(),
    secondLink: CmsLinkSchema.optional(),
    secondLinkIcon: CmsShortTextSchema.optional(),
    // @NOTE hardcoded logic to allow triggering the chat widget - to be replaced with Next Middleware
    chatLink: CmsLinkSchema.optional(),
    chatLinkIcon: CmsShortTextSchema.optional(),
  }),
  configuration: z
    .object({
      backgroundColor: z.string().optional(),
      fontColor: z.string().optional(),
      trackAsContactCard: z.boolean().optional(),
    })
    .optional(),
};
export type CalloutCardTranslatedEntities = SchemaType<(typeof CalloutCardSchema)['translatedEntities']>;
export type CalloutCardConfiguration = SchemaType<(typeof CalloutCardSchema)['configuration']>;

// @TODO setting cta and links WITHOUT icons
export const CalloutCard: ContentComponent<
  CalloutCardTranslatedEntities,
  CalloutCardConfiguration,
  CalloutCardProps
> = function CalloutCard({ translatedEntities, configuration, className, ...props }): JSX.Element {
  const {
    heading,
    body,
    ctaLink,
    ctaIcon = { value: 'arrows/arrow-right' },
    chatLink,
    chatLinkIcon = { value: 'arrows/arrow-right' },
    firstLink,
    firstLinkIcon = { value: 'arrows/arrow-right' },
    secondLink,
    secondLinkIcon = { value: 'arrows/arrow-right' },
  } = translatedEntities;
  const { backgroundColor, fontColor, trackAsContactCard } = {
    ...defaultConfiguration,
    ...configuration,
  };

  const {
    extraSmall,
    small,
    medium,
    mediumStart,
    large,
    extraLarge,
    extraSmallStart,
    smallStart,
    largeStart,
    extraLargeStart,
  } = props;

  let style: Writable<React.CSSProperties> | undefined = {};
  if (backgroundColor) {
    style = {
      ...style,
      backgroundColor,
    };
  }
  if (fontColor) {
    style = {
      ...style,
      color: fontColor,
    };
  }
  if (!Object.keys(style).length) {
    style = undefined;
  }

  /**
   * Heading can be ShortText or RichText
   */
  const headingValue = useMemo(
    () => ('content' in heading ? renderRichTextContent(heading.content) : heading.value),
    [heading]
  );
  return (
    <ContentComponentSection
      className={classNames(CalloutCard.displayName, styles.component, className)}
      {...props}
    >
      <Row>
        <Col
          className={styles.componentCol}
          {...{
            extraSmall,
            small,
            medium,
            mediumStart,
            large,
            extraLarge,
            extraSmallStart,
            smallStart,
            largeStart,
            extraLargeStart,
          }}
        >
          <Card className={styles.card} cornerRadius={1} style={style}>
            <HeadingLevels
              className={classNames('HeadingLevels', styles.heading)}
              semanticLevel={1}
              styleLevel={1}
            >
              {headingValue}
            </HeadingLevels>
            <div className={styles.contents}>
              <div className={styles.body}>{body?.value}</div>
              <div className={styles.ctas}>
                {ctaLink && (
                  <Button
                    className={styles.mainCta}
                    href={ctaLink.url}
                    designType={DesignType.PRIMARY}
                    size="L"
                    iconId={ctaIcon?.value}
                    noIcon={!ctaIcon?.value}
                    onClick={() => {
                      if (trackAsContactCard)
                        sendInternalAnalytic(AnalyticsServiceFieldKey.CALLOUT_CARD_PHONE_CLICKED);
                    }}
                  >
                    {ctaLink.text}
                  </Button>
                )}
                {/* @NOTE hardcoded logic to allow triggering the chat widget - to be replaced with Next Middleware */}
                {chatLink && (
                  <Button
                    designType={DesignType.SECONDARY_LIGHT}
                    size="M"
                    iconId={chatLinkIcon?.value}
                    noIcon={!chatLinkIcon?.value}
                    onClick={() => {
                      if (trackAsContactCard) {
                        sendInternalAnalytic(AnalyticsServiceFieldKey.CALLOUT_CARD_LIVE_CHAT_CLICKED);
                        showLiveChat();
                      }
                    }}
                  >
                    {chatLink.text}
                  </Button>
                )}
                {!chatLink && firstLink && (
                  <Button
                    href={firstLink.url}
                    designType={DesignType.SECONDARY_LIGHT}
                    size="M"
                    iconId={firstLinkIcon?.value}
                    noIcon={!firstLinkIcon?.value}
                  >
                    {firstLink.text}
                  </Button>
                )}
                {secondLink && (
                  <Button
                    href={secondLink.url}
                    designType={DesignType.SECONDARY_LIGHT}
                    size="M"
                    iconId={secondLinkIcon?.value}
                    noIcon={!secondLinkIcon?.value}
                    onClick={() => {
                      if (trackAsContactCard)
                        sendInternalAnalytic(AnalyticsServiceFieldKey.CALLOUT_CARD_EMAIL_CLICKED);
                    }}
                  >
                    {secondLink.text}
                  </Button>
                )}
                <Img
                  className={styles.lines}
                  provider="cloudinary"
                  src="/double_line_horizontal_long_yww6wu.svg"
                  alt=""
                  height={202}
                  width={1164}
                />
              </div>
            </div>
          </Card>
        </Col>
      </Row>
    </ContentComponentSection>
  );
};
CalloutCard.displayName = 'CalloutCard';
CalloutCard.schema = CalloutCardSchema;
